/**
 * Posicionamento da timeline operacional.
 *
 * Entregas de um único dia ocupam a coluna do dia (legíveis, empilháveis).
 * Barras multi-dia ocupam as colunas que atravessam. O motor de layout fala em
 * colunas CSS Grid — não em percentuais da janela — para o chip nunca sair
 * cortado pela borda da célula.
 */

export const DAY_MS = 86_400_000;
export const TIMELINE_MAX_DAY_STACK = 4;

export interface TimelineLayoutInput {
  id: string;
  startMs: number;
  endMs: number;
  isAllDay: boolean;
}

export interface TimelineLayoutItem {
  id: string;
  /** Coluna 1-based no CSS Grid. */
  colStart: number;
  colSpan: number;
  row: number;
  clippedStart: boolean;
  clippedEnd: boolean;
  kind: "event" | "overflow";
  overflowCount: number;
  dayIndex: number | null;
  startMs: number;
}

export function layoutTimelineItems(
  items: TimelineLayoutInput[],
  windowStartMs: number,
  days: number,
  options?: { maxDayStack?: number; expandedDays?: ReadonlySet<number> },
): TimelineLayoutItem[] {
  const maxDayStack = options?.maxDayStack ?? TIMELINE_MAX_DAY_STACK;
  const expandedDays = options?.expandedDays ?? new Set<number>();
  const totalMs = days * DAY_MS;
  const windowEndMs = windowStartMs + totalMs;

  const events: TimelineLayoutItem[] = [];

  for (const item of items) {
    if (item.endMs <= windowStartMs || item.startMs >= windowEndMs) continue;

    const clampedStart = Math.max(item.startMs, windowStartMs);
    const clampedEnd = Math.min(item.endMs, windowEndMs);
    const duration = Math.max(0, clampedEnd - clampedStart);
    const clippedStart = item.startMs < windowStartMs;
    const clippedEnd = item.endMs > windowEndMs;
    const spans = item.isAllDay ? duration >= DAY_MS * 0.85 : duration >= DAY_MS * 0.9;

    if (spans) {
      const colStart = Math.min(
        days,
        Math.max(1, Math.floor((clampedStart - windowStartMs) / DAY_MS) + 1),
      );
      const colEndExclusive = Math.min(
        days + 1,
        Math.max(colStart + 1, Math.ceil((clampedEnd - windowStartMs) / DAY_MS) + 1),
      );
      events.push({
        id: item.id,
        colStart,
        colSpan: Math.max(1, colEndExclusive - colStart),
        row: 0,
        clippedStart,
        clippedEnd,
        kind: "event",
        overflowCount: 0,
        dayIndex: null,
        startMs: item.startMs,
      });
      continue;
    }

    const dayIndex = Math.min(
      days - 1,
      Math.max(0, Math.floor((clampedStart - windowStartMs) / DAY_MS)),
    );
    events.push({
      id: item.id,
      colStart: dayIndex + 1,
      colSpan: 1,
      row: 0,
      clippedStart: false,
      clippedEnd: false,
      kind: "event",
      overflowCount: 0,
      dayIndex,
      startMs: item.startMs,
    });
  }

  const limited: TimelineLayoutItem[] = [];
  const dayBuckets = new Map<number, TimelineLayoutItem[]>();

  for (const event of events) {
    if (event.dayIndex === null) {
      limited.push(event);
      continue;
    }
    const bucket = dayBuckets.get(event.dayIndex) ?? [];
    bucket.push(event);
    dayBuckets.set(event.dayIndex, bucket);
  }

  for (const [dayIndex, bucket] of dayBuckets) {
    bucket.sort((a, b) => a.startMs - b.startMs || a.id.localeCompare(b.id));
    const expanded = expandedDays.has(dayIndex);
    const cap = expanded ? bucket.length : maxDayStack;
    const shown = bucket.slice(0, Math.max(0, cap));
    const extra = bucket.length - shown.length;
    limited.push(...shown);
    if (extra > 0) {
      limited.push({
        id: `__overflow-${dayIndex}`,
        colStart: dayIndex + 1,
        colSpan: 1,
        row: 0,
        clippedStart: false,
        clippedEnd: false,
        kind: "overflow",
        overflowCount: extra,
        dayIndex,
        startMs: shown[shown.length - 1]?.startMs ?? bucket[0]?.startMs ?? 0,
      });
    }
  }

  packRows(limited);
  return limited;
}

function packRows(items: TimelineLayoutItem[]): void {
  items.sort((a, b) => {
    if (a.colStart !== b.colStart) return a.colStart - b.colStart;
    if (a.kind !== b.kind) return a.kind === "overflow" ? 1 : -1;
    if (a.colSpan !== b.colSpan) return b.colSpan - a.colSpan;
    return a.startMs - b.startMs;
  });

  const rowEnds: number[] = [];
  for (const item of items) {
    const itemEnd = item.colStart + item.colSpan;
    let row = rowEnds.findIndex((end) => end <= item.colStart);
    if (row === -1) {
      row = rowEnds.length;
      rowEnds.push(itemEnd);
    } else {
      rowEnds[row] = itemEnd;
    }
    item.row = row;
  }
}
